// src/components/item-builder/ClozeRegexModal.jsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { X, Plus, Trash2 } from "lucide-react";
import { httpRequest } from "../../utils/httpRequest";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

// همون تابعی که در ClozeModal هم داشتی:
function replaceClozePlaceholders(html, startIndex = 1) {
  if (typeof html !== "string") return { text: "", count: 0 };
  let i = startIndex - 1;
  // [], [ ], [blank], [ تهی ], ...
  const rx =
    /\[\s*(?:blank|تهی|EMPTY|empty|فراغ|BLANK|[A-Za-z\u0600-\u06FF_]*)?\s*\]/g;
  const text = html.replace(rx, () => {
    i += 1;
    return `{{${i}}}`;
  });
  return { text, count: i - (startIndex - 1) };
}

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  text_fa: "",
  text_en: "",
  clozeRegexes: [{ placeholder_fa: "", placeholder_en: "", pattern: "" }],
  layout: "top-bottom",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";

  const body = item.body || {};
  const layout = body.layout || "top-bottom";

  const text_fa = body?.text?.fa || "";
  const text_en = body?.text?.en || "";

  const clozeRegexesRaw = Array.isArray(body.clozeRegexes)
    ? body.clozeRegexes
    : [];

  const clozeRegexes =
    clozeRegexesRaw.length > 0
      ? clozeRegexesRaw.map((r) => ({
          placeholder_fa: r?.placeholder?.fa || "",
          placeholder_en: r?.placeholder?.en || "",
          pattern: r?.pattern || "",
        }))
      : [{ placeholder_fa: "", placeholder_en: "", pattern: "" }];

  const points = item.points ?? 1;
  const status = item.status || "published";
  const tags = Array.isArray(item.tags) ? item.tags.join(",") : "";
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    text_fa,
    text_en,
    clozeRegexes,
    layout,
    points,
    status,
    tags,
    explanation_fa,
    explanation_en,
  };
};

export default function ClozeRegexModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create",
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  const addClozeRegex = () => {
    setForm((f) => ({
      ...f,
      clozeRegexes: [
        ...f.clozeRegexes,
        { placeholder_fa: "", placeholder_en: "", pattern: "" },
      ],
    }));
  };

  const removeClozeRegex = (idx) => {
    setForm((f) => ({
      ...f,
      clozeRegexes: f.clozeRegexes.filter((_, i) => i !== idx),
    }));
  };

  const handleRegexChange = (idx, field, value) => {
    setForm((f) => {
      const regexes = [...f.clozeRegexes];
      regexes[idx] = { ...regexes[idx], [field]: value };
      return { ...f, clozeRegexes: regexes };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("cloze_regex.toasts.stem_required"));
      return;
    }
    if (!form.text_fa.trim() && !form.text_en.trim()) {
      toast.error(t("cloze_regex.toasts.text_required", "متن سوال لازم است"));
      return;
    }

    const validRegexes = form.clozeRegexes.filter((r) => r.pattern.trim());
    if (validRegexes.length === 0) {
      toast.error(t("cloze_regex.toasts.regex_required"));
      return;
    }

    // تبدیل [] به {{1}}, {{2}}, ...
    const faConv = replaceClozePlaceholders(form.text_fa, 1);
    const enConv = replaceClozePlaceholders(form.text_en, 1);
    const blanksCount = Math.max(faConv.count, enConv.count);

    if (blanksCount === 0) {
      toast.error(
        t("cloze_regex.toasts.no_placeholders") ||
          "هیچ جای خالی در متن نیست (از [] استفاده کن)."
      );
      return;
    }

    if (validRegexes.length < blanksCount) {
      toast.error(
        t("cloze_regex.toasts.not_enough_regex", {
          defaultValue: "تعداد الگوها (Regex) از تعداد جای‌خالی‌ها کمتر است.",
        })
      );
      return;
    }

    // فقط به تعداد blanksCount از regexها استفاده می‌کنیم
    const normalizedRegexes = validRegexes.slice(0, blanksCount).map((r) => ({
      placeholder: {
        fa: r.placeholder_fa || undefined,
        en: r.placeholder_en || undefined,
      },
      pattern: r.pattern,
    }));

    try {
      const payload = {
        kind: "cloze_regex",
        explanation: {
          fa: form.explanation_fa || undefined,
          en: form.explanation_en || undefined,
        },
        stem: {
          fa: form.stem_fa || undefined,
          en: form.stem_en || undefined,
        },
        body: {
          text: {
            fa: faConv.text || undefined,
            en: enConv.text || undefined,
          },
          clozeRegexes: normalizedRegexes,
          layout: form.layout,
        },
        points: Number(form.points) || 1,
        status: form.status || "draft",
        tags: form.tags
          ? form.tags
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
      };

      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t(
              "cloze_regex.toasts.updated",
              "سؤال کلوز رگِکس با موفقیت ویرایش شد"
            )
          : t("cloze_regex.toasts.created")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  if (!isOpen) return null;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Title + Close */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg md:text-xl font-semibold">
          {t("cloze_regex.title", "Cloze Regex")}
        </h2>
        <button
          type="button"
          className="p-2 rounded-lg hover:bg-gray-100"
          onClick={onClose}
        >
          <X />
        </button>
      </div>

      {/* Layout */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t("cloze_regex.layout_label")}
        </label>
        <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
          {["top-bottom", "bottom-top", "left-right", "right-left"].map(
            (layout) => (
              <div key={layout} className="flex items-center">
                <input
                  type="radio"
                  id={`layout-${layout}`}
                  name="layout"
                  value={layout}
                  checked={form.layout === layout}
                  onChange={onChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />
                <label
                  htmlFor={`layout-${layout}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {t(`cloze_regex.layout_question_answer_${layout}`)}
                </label>
              </div>
            )
          )}
        </div>
      </div>

      {/* Stem */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("cloze_regex.stem_fa")}
          </label>
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) => handleEditorChange("stem_fa", content)}
            placeholder={t("cloze_regex.stem_fa_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("cloze_regex.stem_en")}
          </label>
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) => handleEditorChange("stem_en", content)}
            placeholder={t("cloze_regex.stem_en_ph")}
          />
        </div>
      </div>

      {/* Text (Passage with []) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("cloze_regex.text_fa", "متن (فارسی)")}
          </label>
          <CustomEditor
            value={form.text_fa}
            onEditorChange={(content) => handleEditorChange("text_fa", content)}
            placeholder={t(
              "cloze_regex.text_fa_ph",
              "برای جای‌خالی از [] استفاده کن"
            )}
            directionality={isRtl ? "rtl" : "ltr"}
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("cloze_regex.text_en", "Text (English)")}
          </label>
          <CustomEditor
            value={form.text_en}
            onEditorChange={(content) => handleEditorChange("text_en", content)}
            placeholder={t(
              "cloze_regex.text_en_ph",
              "Use [] for blanks in text"
            )}
          />
        </div>
      </div>

      {/* Regex definitions */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium">
            {t("cloze_regex.regex_answers")}
          </label>
          <button
            type="button"
            onClick={addClozeRegex}
            className="text-blue-600 text-sm inline-flex items-center gap-1"
          >
            <Plus size={16} />
            {t("cloze_regex.add_regex_answer")}
          </button>
        </div>
        <div className="space-y-2">
          {form.clozeRegexes.map((item, idx) => (
            <div
              key={idx}
              className="border rounded-lg p-3 space-y-2 bg-gray-50"
            >
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-semibold">
                  {t("cloze_regex.regex_n", {
                    defaultValue: "Regex {{n}}",
                    n: idx + 1,
                  })}
                </span>
                {form.clozeRegexes.length > 1 && (
                  <button
                    type="button"
                    className="p-1 rounded-full text-red-600 hover:bg-red-100"
                    onClick={() => removeClozeRegex(idx)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder={t("cloze_regex.placeholder_fa_ph")}
                  value={item.placeholder_fa}
                  onChange={(e) =>
                    handleRegexChange(idx, "placeholder_fa", e.target.value)
                  }
                />
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder={t("cloze_regex.placeholder_en_ph")}
                  value={item.placeholder_en}
                  onChange={(e) =>
                    handleRegexChange(idx, "placeholder_en", e.target.value)
                  }
                />
              </div>
              <div className="flex gap-2 items-center">
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder={t("cloze_regex.pattern_ph")}
                  value={item.pattern}
                  onChange={(e) =>
                    handleRegexChange(idx, "pattern", e.target.value)
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("cloze_regex.points")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.points}
            onChange={(e) => setForm((f) => ({ ...f, points: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("cloze_regex.status")}
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="published">{t("common.published")}</option>
            <option value="draft">{t("common.draft")}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("cloze_regex.tags")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("common.tags_csv")}
            value={form.tags}
            onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
          />
        </div>
      </div>

      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />

      <div className="mt-4">
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg"
        >
          {isEdit ? t("common.update") : t("common.create")}
        </button>
      </div>
    </form>
  );
}
